import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { DollarSign, Plus, Edit2, Trash2, CheckCircle, XCircle, Eye, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyPayment = {
  student_id: '', month: new Date().toISOString().slice(0, 7),
  amount: '', status: 'unpaid', paid_date: '', payment_method: '', note: '',
};

const statusColor = (s) => ({
  paid: 'bg-[#D1FAE5] text-[#10B981] border-[#10B981]',
  overdue: 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444]',
  unpaid: 'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B]',
  declared: 'bg-blue-100 text-blue-700 border-blue-300',
}[s] || 'bg-slate-100 text-slate-700 border-slate-300');

const verifBadge = (v) => {
  if (!v) return null;
  const map = {
    pending_verification: 'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B]',
    received: 'bg-blue-100 text-blue-700 border-blue-300',
    verified: 'bg-[#D1FAE5] text-[#10B981] border-[#10B981]',
    rejected: 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444]',
  };
  const labels = {
    pending_verification: 'Awaiting Verification',
    received: 'Received',
    verified: 'Verified',
    rejected: 'Rejected',
  };
  return <Badge className={`${map[v]} border text-[10px]`}>{labels[v] || v}</Badge>;
};

const AdminPaymentsSection = () => {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPayment);
  const [detailPayment, setDetailPayment] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        axios.get(`${API}/api/payments`, { withCredentials: true }),
        axios.get(`${API}/api/students`, { withCredentials: true }),
      ]);
      setPayments(p.data);
      setStudents(s.data);
    } catch (e) { toast.error('Failed to load'); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyPayment); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      student_id: p.student_id, month: p.month, amount: p.amount,
      status: p.status === 'overdue' ? 'unpaid' : p.status,
      paid_date: p.paid_date || '', payment_method: p.payment_method || '', note: p.note || '',
    });
    setDialogOpen(true);
  };

  const savePayment = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (editing) {
        await axios.put(`${API}/api/payments/${editing.id}`, payload, { withCredentials: true });
        toast.success('Payment updated');
      } else {
        await axios.post(`${API}/api/payments`, payload, { withCredentials: true });
        toast.success('Payment created');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save');
    }
  };

  const verifyPayment = async (id, verification_status, admin_note = '') => {
    try {
      await axios.post(`${API}/api/payments/${id}/verify`, { verification_status, admin_note }, { withCredentials: true });
      toast.success(`Payment ${verification_status}`);
      fetchAll();
      setDetailPayment(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const deletePayment = async (id) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await axios.delete(`${API}/api/payments/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const declaredPayments = payments.filter((p) => p.verification_status === 'pending_verification');

  const sendReminders = async () => {
    if (!window.confirm('Send email reminders to all parents with unpaid fees for the current month?')) return;
    try {
      const res = await axios.post(
        `${API}/api/payments/send-reminders`,
        { only_overdue: false },
        { withCredentials: true }
      );
      const d = res.data;
      const msg = `Reminders — ${d.sent_count} sent, ${d.failed_count} failed, ${d.skipped_count} skipped`;
      if (d.sent_count > 0) toast.success(msg);
      else toast.info(msg);
      if (d.failed_count > 0 && d.failed[0]?.error) {
        toast.warning(`First failure: ${d.failed[0].error.slice(0, 120)}`, { duration: 8000 });
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminders');
    }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <Card className="bg-white border-slate-200" data-testid="admin-payments-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Monthly Payments</CardTitle>
          <CardDescription>Track parent payments — auto-flags as overdue after end of month</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={sendReminders} data-testid="send-reminders-button">
            <Send className="w-4 h-4 mr-2" /> Send Reminders
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={openCreate} data-testid="new-payment-button">
            <Plus className="w-4 h-4 mr-2" />New Payment
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {declaredPayments.length > 0 && (
          <div className="mb-4 p-3 border border-blue-200 bg-blue-50 rounded-lg" data-testid="declared-banner">
            <p className="text-sm font-semibold text-blue-900">
              {declaredPayments.length} payment {declaredPayments.length === 1 ? 'declaration' : 'declarations'} awaiting your verification
            </p>
          </div>
        )}
        {payments.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-400" />
            <p>No payment records yet</p>
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Student</TableHead><TableHead>Month</TableHead><TableHead className="text-right">Amount</TableHead>
              <TableHead>Due Date</TableHead><TableHead>Paid Date</TableHead><TableHead>Method</TableHead>
              <TableHead>Status</TableHead><TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody data-testid="admin-payments-table">
              {payments.map((p, idx) => (
                <TableRow key={p.id} data-testid={`admin-payment-row-${idx}`}>
                  <TableCell className="font-medium">{p.student_name}</TableCell>
                  <TableCell>{p.month}</TableCell>
                  <TableCell className="text-right">RM {p.amount?.toFixed(2)}</TableCell>
                  <TableCell className="text-sm text-slate-600">{p.due_date || '—'}</TableCell>
                  <TableCell className="text-sm text-slate-600">{p.paid_date || '—'}</TableCell>
                  <TableCell className="text-sm text-slate-600">{p.payment_method || '—'}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge className={`${statusColor(p.status)} border`}>{p.status}</Badge>
                      {verifBadge(p.verification_status)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {p.verification_status === 'pending_verification' && (
                        <>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => verifyPayment(p.id, 'verified')} data-testid={`verify-payment-${idx}`} title="Verify">
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => verifyPayment(p.id, 'rejected')} data-testid={`reject-payment-${idx}`} title="Reject">
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {p.verification_status && (
                        <Button variant="outline" size="sm" onClick={() => setDetailPayment(p)} data-testid={`view-payment-${idx}`} title="View details"><Eye className="w-4 h-4" /></Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-payment-${idx}`}><Edit2 className="w-4 h-4" /></Button>
                      <Button variant="outline" size="sm" onClick={() => deletePayment(p.id)} className="text-red-600" data-testid={`delete-payment-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editing ? 'Edit' : 'New'} Payment</DialogTitle>
            <DialogDescription>Payments must be made by the end of each month</DialogDescription>
          </DialogHeader>
          <form onSubmit={savePayment} className="space-y-3" data-testid="payment-form">
            <div>
              <Label>Student</Label>
              <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })} disabled={!!editing}>
                <SelectTrigger data-testid="payment-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Month</Label><Input type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} required data-testid="payment-month-input" /></div>
              <div><Label>Amount (RM)</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="payment-amount-input" /></div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.status === 'paid' && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Paid Date</Label><Input type="date" value={form.paid_date} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} data-testid="payment-paid-date-input" /></div>
                <div><Label>Payment Method</Label><Input value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} placeholder="Cash, Bank, etc." data-testid="payment-method-input" /></div>
              </div>
            )}
            <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} data-testid="payment-note-input" /></div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-payment-button">{editing ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment declaration details */}
      <Dialog open={!!detailPayment} onOpenChange={(o) => !o && setDetailPayment(null)}>
        <DialogContent className="max-w-md" data-testid="payment-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Payment Declaration Details</DialogTitle>
            <DialogDescription>{detailPayment?.student_name} • {detailPayment?.month}</DialogDescription>
          </DialogHeader>
          {detailPayment && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Amount</p>
                  <p className="text-slate-900 font-medium">RM {detailPayment.amount?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Method</p>
                  <p className="text-slate-900">{detailPayment.payment_method || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Paid Date</p>
                  <p className="text-slate-900">{detailPayment.paid_date || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Reference</p>
                  <p className="text-slate-900 font-mono text-xs break-all">{detailPayment.payment_reference || '—'}</p>
                </div>
              </div>
              {detailPayment.parent_note && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Parent Note</p>
                  <p className="text-slate-700 whitespace-pre-wrap">{detailPayment.parent_note}</p>
                </div>
              )}
              {detailPayment.verified_by && (
                <div className="text-xs text-slate-500 border-t pt-2">
                  Last action by {detailPayment.verified_by} on {detailPayment.verified_at ? new Date(detailPayment.verified_at).toLocaleString() : ''}
                </div>
              )}
              {detailPayment.verification_status === 'pending_verification' && (
                <div className="flex gap-2 pt-2 border-t">
                  <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={() => verifyPayment(detailPayment.id, 'received')} data-testid="mark-received-button">
                    Mark Received
                  </Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => verifyPayment(detailPayment.id, 'verified')} data-testid="verify-detail-button">
                    Verify
                  </Button>
                  <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={() => verifyPayment(detailPayment.id, 'rejected')} data-testid="reject-detail-button">
                    Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminPaymentsSection;
